/**
 * Transcript Context
 * 
 * Manages conversation transcript state globally across the app.
 * Provides real-time message storage and panel control.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export interface TranscriptMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  agentId?: string; // Which agent spoke (for assistant messages)
}

interface TranscriptContextValue {
  messages: TranscriptMessage[];
  addMessage: (message: Omit<TranscriptMessage, 'id' | 'timestamp'>) => void;
  clearTranscript: () => void;
  isPanelOpen: boolean;
  togglePanel: () => void;
  unreadCount: number;
  markAsRead: () => void;
}

const TranscriptContext = createContext<TranscriptContextValue | undefined>(undefined);

const MAX_MESSAGES = 100; // Limit history to prevent memory issues
const STORAGE_KEY = 'saaed_transcript';
const PANEL_STATE_KEY = 'saaed_transcript_panel_open';

interface TranscriptProviderProps {
  children: ReactNode;
}

export const TranscriptProvider: React.FC<TranscriptProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(() => {
    // Load panel state from localStorage
    const saved = localStorage.getItem(PANEL_STATE_KEY);
    return saved ? JSON.parse(saved) : false;
  });
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // Load transcript from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert timestamp strings back to Date objects
        const messagesWithDates = parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
        setMessages(messagesWithDates);
      }
    } catch (error) {
      console.error('[Transcript] Error loading from localStorage:', error);
    }
  }, []);

  // Save transcript to localStorage whenever it changes
  useEffect(() => {
    try {
      if (messages.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
      }
    } catch (error) {
      console.error('[Transcript] Error saving to localStorage:', error);
    }
  }, [messages]);

  // Save panel state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(PANEL_STATE_KEY, JSON.stringify(isPanelOpen));
  }, [isPanelOpen]);

  const addMessage = useCallback((message: Omit<TranscriptMessage, 'id' | 'timestamp'>) => {
    const newMessage: TranscriptMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    setMessages((prev) => {
      const updated = [...prev, newMessage];
      // Trim to max messages
      return updated.length > MAX_MESSAGES ? updated.slice(-MAX_MESSAGES) : updated;
    });

    // Increment unread count if panel is closed
    if (!isPanelOpen) {
      setUnreadCount((prev) => prev + 1);
    }

    console.log('[Transcript] Message added:', {
      role: newMessage.role,
      agentId: newMessage.agentId,
      content: newMessage.content.substring(0, 50) + '...',
    });
  }, [isPanelOpen]);

  const clearTranscript = useCallback(() => {
    setMessages([]);
    setUnreadCount(0);
    localStorage.removeItem(STORAGE_KEY);
    console.log('[Transcript] Cleared');
  }, []);

  const togglePanel = useCallback(() => {
    setIsPanelOpen((prev) => {
      const newState = !prev;
      // Reset unread count when opening
      if (newState) {
        setUnreadCount(0);
      }
      return newState;
    });
  }, []);

  const markAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const value: TranscriptContextValue = {
    messages,
    addMessage,
    clearTranscript,
    isPanelOpen,
    togglePanel,
    unreadCount,
    markAsRead,
  };

  return (
    <TranscriptContext.Provider value={value}>
      {children}
    </TranscriptContext.Provider>
  );
};

export const useTranscript = (): TranscriptContextValue => {
  const context = useContext(TranscriptContext);
  if (!context) {
    throw new Error('useTranscript must be used within a TranscriptProvider');
  }
  return context;
};
